import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { FaUserCircle, FaLock } from "react-icons/fa";
import EmailPreferences from "./EmailPreferences";
import "./ProfilePage.css";

/* ===============================
   DEV LOGGER (NO LOGS IN PROD)
=============================== */
const devLog = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.log("[ProfilePage]", ...args);
  }
};

const ProfilePage = () => {

  const [user, setUser] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  /* ===============================
     LOAD USER OR REDIRECT
  =============================== */
  useEffect(() => {

    devLog("Checking stored user and token");

    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!storedUser || !token) {

      devLog("User or token missing → redirecting to register");

      navigate("/register", { replace: true });

    } else {

      try {

        const parsedUser = JSON.parse(storedUser);

        devLog("User loaded successfully", {
          id: parsedUser.id,
          email: parsedUser.email,
          name: parsedUser.name,
          googleUser: Boolean(parsedUser.googleId)
        });

        setUser(parsedUser);

      } catch (error) {

        devLog("Failed to parse stored user", error);

        navigate("/register", { replace: true });
      }
    }

  }, [navigate]);


  if (!user) {
    devLog("User not ready yet → rendering null");
    return null;
  }

  const isGoogleUser = Boolean(user.googleId);


  /* ===============================
     PASSWORD RESET HANDLER
  =============================== */
  const handlePasswordReset = async () => {

    devLog("Password reset initiated");

    setLoading(true);
    setMessage("");

    try {

      await new Promise((res) => setTimeout(res, 1200));

      devLog("Password reset simulated successfully");

      setMessage("Password reset instructions sent to your email.");

    } catch (err) {

      devLog("Password reset failed", err);

      setMessage("Failed to send reset email. Try again later.");

    } finally {

      setLoading(false);
    }
  };


  /* ===============================
     NAVIGATE TO RESET PAGE
  =============================== */
  const handleNavigateToReset = () => {

    devLog("Navigating to forgot-password page", {
      email: user.email
    });

    navigate("/forgot-password", {
      state: {
        mode: "reset",
        email: user.email,
        fromProfile: true
      }
    });
  };


  /* ===============================
     NAVIGATE TO REFERRAL DASHBOARD
  =============================== */
  const handleNavigateToReferral = () => {

    devLog("Navigating to referral dashboard");

    navigate("/referral");
  };


  return (
  <>
    <Navbar />

    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-card">

          {/* HEADER SECTION */}
          <div className="profile-header">

            <div className="profile-avatar-wrapper">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="profile-avatar"
                />
              ) : (
                <div className="profile-avatar-fallback">
                  {user.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
              )}
            </div>

            <div className="profile-info">
              <h2 className="profile-name">
                {user.name || "User"}
              </h2>
              <p className="profile-email">{user.email}</p>

              <span className={`profile-badge ${isGoogleUser ? "google" : "email"}`}>
                {isGoogleUser ? "Google Account" : "Email Account"}
              </span>
            </div>

          </div>

          <div className="divider" />

          {/* SECURITY SECTION */}
          <div className="profile-section">

            <div className="section-header">
              <h3>Security</h3>
            </div>

            <button
              className="btn-outline"
              disabled={isGoogleUser}
              onClick={() => {
                if (!isGoogleUser) {
                  handleNavigateToReset();
                }
              }}
            >
              <FaLock /> Reset Password
            </button>

            {isGoogleUser && (
              <p className="hint">
                Password is managed securely via Google.
              </p>
            )}
          </div>

          <div className="divider" />

          {/* EMAIL PREFERENCES SECTION */}
          <div className="profile-section">
            <EmailPreferences />
          </div>

          <div className="divider" />

          {/* REFERRAL SECTION */}
          <div className="profile-section">

            <div className="section-header">
              <h3>Referral Program</h3>
              <span className="section-tag">Earn Rewards</span>
            </div>

            <p className="hint">
              Invite friends and earn free speaking sessions.
            </p>

            <button
              className="btn-primary"
              onClick={handleNavigateToReferral}
            >
              View Referral Dashboard
            </button>
          </div>

        </div>
      </div>
    </div>
  </>
);
};

export default ProfilePage;
